import type { ReactNode } from 'react';
import type { OrderTaskCargoItem, OrderTaskCargoItemProduct } from '@my/tms-api';
import { deliveryUnitSlot, productLineLabel } from './allocation';
import { cargoHandlingPhaseFor, UNIT_IDENTIFIER_FAMILIES } from './cargoPick';
import { clearancePill, DataTagList, podPill, StatusPill } from '../components/badges';
import { podStateFor, type PodState } from './pod';
import type {
  CargoFamily,
  CellSpec,
  MockBooking,
  MockTask,
  TaskPanelContext,
} from './types';

export const CELL_DASH = '—';

/**
 * Per-task-type × cargo-family column set for the data strip. Booking
 * halves are cargo-agnostic; process events (Stop / Instruction /
 * Inspection) show only Notes; everything else routes to a family-
 * specific cell builder.
 */
export function getTaskDataCells(
  task: MockTask,
  bicCount: number,
  cargoFamily: CargoFamily,
  panelContext: TaskPanelContext,
  booking: MockBooking | null = null,
): CellSpec[] {
  if (task.type === 'BookingDeparture') return bookingDepartureCells(booking);
  if (task.type === 'BookingArrival') return bookingArrivalCells(booking, task, cargoFamily);
  if (task.type === 'Stop' || task.type === 'Instruction' || task.type === 'Inspection') {
    // A wash / tank clean is done TO a tank, so it names the tank as well.
    const raw = task._source?.type;
    if (cargoFamily === 'tank' && (raw === 'Wash' || raw === 'TankClean')) {
      return [
        { label: 'Tank ID', values: tankIdsAtTask(task, bicCount, panelContext), perUnit: true },
        ...processEventCells(task),
      ];
    }
    return processEventCells(task);
  }
  if (cargoFamily === 'tank') return tankFamilyCells(task, bicCount, panelContext);
  if (cargoFamily === 'pallet') return palletFamilyCells(task, panelContext);
  return containerFamilyCells(task, bicCount, panelContext);
}

/** Single Notes cell for process-only tasks (no cargo identifiers, no seals, no POD). */
export function processEventCells(task: MockTask): CellSpec[] {
  const notes = task._source?.notes?.trim();
  return [{ label: 'Notes', values: [notes || CELL_DASH], perUnit: false }];
}

/** O(1) cargo-item lookup by 1-based itemIndex, formatted or em-dashed. */
export function cargoFieldAt(
  task: MockTask,
  itemIndex: number,
  picker: (item: OrderTaskCargoItem) => unknown,
): string {
  const items = task._source?.cargoItems ?? [];
  return cellText(items.find((c) => c.itemIndex === itemIndex), picker);
}

function cellText(
  item: OrderTaskCargoItem | undefined,
  picker: (item: OrderTaskCargoItem) => unknown,
): string {
  if (!item) return CELL_DASH;
  const raw = picker(item);
  if (raw == null) return CELL_DASH;
  if (typeof raw === 'string') return raw.trim() === '' ? CELL_DASH : raw;
  return String(raw);
}

/**
 * The row carrying a physical unit's own fields (Tank ID / BIC, seal, tank
 * status, capacity). On a container / tank DELIVERY the rows are numbered in
 * allocation order, so the unit is found through the collection unit each row
 * draws from (`deliveryUnitSlot`) — the same rule the task form hydrates its
 * unit inputs with. Everywhere else a unit is its `itemIndex`.
 */
function unitRowAt(
  task: MockTask,
  unit: number,
  unitCount: number,
  panelContext: TaskPanelContext,
): OrderTaskCargoItem | undefined {
  const items = task._source?.cargoItems ?? [];
  const delivery =
    cargoHandlingPhaseFor(task._source?.type ?? 'PickUp') === 'delivery' &&
    UNIT_IDENTIFIER_FAMILIES.has(panelContext.cargoFamily);
  if (!delivery) return items.find((c) => c.itemIndex === unit);
  return items.find(
    (c) => deliveryUnitSlot(c, panelContext.collectionItems, unitCount) === unit,
  );
}

/** `cargoFieldAt` for the unit's own fields — see `unitRowAt`. */
function unitFieldAt(
  task: MockTask,
  unit: number,
  unitCount: number,
  panelContext: TaskPanelContext,
  picker: (item: OrderTaskCargoItem) => unknown,
): string {
  return cellText(unitRowAt(task, unit, unitCount, panelContext), picker);
}

/**
 * Tags cell. Per-task `serviceTagIds` wins over the order-level default
 * — covers operator-narrowed per-leg tag sets while still inheriting on
 * legacy rows.
 */
export function tagsCellSpec(task: MockTask, panelContext: TaskPanelContext): CellSpec {
  const sourceIds =
    task._source?.serviceTagIds && task._source.serviceTagIds.length > 0
      ? task._source.serviceTagIds
      : panelContext.orderActiveTagIds;
  const activeIds = new Set(sourceIds);
  const tagNames = panelContext.serviceTagsCatalogue
    .filter((t) => activeIds.has(t.id))
    .map((t) => t.name);
  if (tagNames.length === 0) {
    return { label: 'Tags', values: [CELL_DASH], perUnit: false };
  }
  return { label: 'Tags', values: [<DataTagList tags={tagNames} />], perUnit: false };
}

/**
 * The POD state of a leg, read off the BE row behind the display object.
 *
 * <p>
 * `_source` is absent only for a task that has never been saved, and an unsaved
 * leg has no document attached to it either — so no POD, which is what null
 * says.
 * </p>
 */
function podStateOf(task: MockTask, panelContext: TaskPanelContext): PodState | null {
  const source = task._source;
  if (!source) return null;
  return podStateFor(source, panelContext.podCoverage);
}

export function containerFamilyCells(
  task: MockTask,
  bicCount: number,
  panelContext: TaskPanelContext,
): CellSpec[] {
  const bicValues: string[] = [];
  const sealValues: string[] = [];
  const weightValues: string[] = [];
  const volumeValues: string[] = [];
  const sealStatusValues: string[] = [];
  for (let unit = 1; unit <= bicCount; unit++) {
    bicValues.push(unitFieldAt(task, unit, bicCount, panelContext, (c) => c.identifier));
    sealValues.push(unitFieldAt(task, unit, bicCount, panelContext, (c) => c.seal));
    weightValues.push(
      cargoFieldAt(task, unit, (c) =>
        c.weightKg != null ? `${c.weightKg.toLocaleString('en-IE')} kg` : null,
      ),
    );
    volumeValues.push(
      cargoFieldAt(task, unit, (c) => (c.volumeM3 != null ? `${c.volumeM3} m³` : null)),
    );
    sealStatusValues.push(unitFieldAt(task, unit, bicCount, panelContext, (c) => c.sealStatus));
  }

  // When the cargo type carries products, Product takes Volume's place: what
  // each container carries on a load, what comes off it on a delivery
  // (resolved from the collection it draws from). Otherwise Volume stays.
  const showProduct = panelContext.productOptions != null;
  const fallbackDescription: string =
    task._source?.cargoItems
      ?.slice()
      .sort((a, b) => a.itemIndex - b.itemIndex)
      .map((c) => c.description?.trim())
      .find((d) => !!d) ?? CELL_DASH;
  const productsByUnit = taskProductsByUnit(task, bicCount, panelContext);
  const productValues: (string | ReactNode)[] = [];
  for (let unit = 1; unit <= bicCount; unit++) {
    productValues.push(productCellValue(productsByUnit.get(unit) ?? [], fallbackDescription));
  }

  // Load-side captures seal applied + weight loaded. Delivery-side
  // verifies seal status + offload weight + POD.
  const isLoadSide = cargoHandlingPhaseFor(task._source?.type ?? 'PickUp') === 'load';
  if (isLoadSide) {
    return [
      { label: 'BIC Code', values: bicValues, perUnit: true },
      { label: 'Seal', values: sealValues, perUnit: true },
      { label: 'Weight', values: weightValues, perUnit: true },
      showProduct
        ? { label: 'Product', values: productValues, perUnit: true }
        : { label: 'Volume', values: volumeValues, perUnit: true },
      tagsCellSpec(task, panelContext),
    ];
  }
  // Everything coming off each container — several products off one unit
  // are several delivery rows but one Offload.
  const offloadByUnit = deliveryWeightByUnit(task, bicCount, panelContext);
  const offloadValues: string[] = [];
  for (let unit = 1; unit <= bicCount; unit++) {
    const kg = offloadByUnit.get(unit);
    offloadValues.push(kg != null ? `${kg.toLocaleString('en-IE')} kg` : CELL_DASH);
  }
  return [
    { label: 'BIC Code', values: bicValues, perUnit: true },
    { label: 'Seal status', values: sealStatusValues, perUnit: true },
    { label: 'Offload', values: offloadValues, perUnit: true },
    showProduct
      ? { label: 'Product', values: productValues, perUnit: true }
      : { label: 'Volume', values: volumeValues, perUnit: true },
    { label: 'POD', values: [podPill(task.state, podStateOf(task, panelContext))], perUnit: false },
  ];
}

export function tankFamilyCells(
  task: MockTask,
  bicCount: number,
  panelContext: TaskPanelContext,
): CellSpec[] {
  const tankIdValues: string[] = [];
  const tankStatusValues: (string | ReactNode)[] = [];
  const capacityValues: string[] = [];
  const weightValues: string[] = [];
  for (let unit = 1; unit <= bicCount; unit++) {
    tankIdValues.push(unitFieldAt(task, unit, bicCount, panelContext, (c) => c.identifier));
    const status = unitRowAt(task, unit, bicCount, panelContext)?.tankStatus;
    if (status === 'Full') {
      tankStatusValues.push(<StatusPill tone="green" label="Full" />);
    } else if (status === 'EmptyClean') {
      tankStatusValues.push(<StatusPill tone="blue" label="Empty (clean)" />);
    } else if (status === 'EmptyDirty') {
      tankStatusValues.push(<StatusPill tone="amber" label="Empty (dirty)" />);
    } else {
      tankStatusValues.push(CELL_DASH);
    }
    capacityValues.push(
      cargoFieldAt(task, unit, (c) =>
        c.capacity != null ? `${c.capacity.toLocaleString('en-IE')} L` : null,
      ),
    );
    weightValues.push(
      cargoFieldAt(task, unit, (c) =>
        c.weightKg != null ? `${c.weightKg.toLocaleString('en-IE')} kg` : null,
      ),
    );
  }

  // What each tank carries — its product lines on a collection, the lines
  // it draws from on a drop-off. Falls back to the first description for
  // rows saved before products existed.
  const fallbackDescription: string =
    task._source?.cargoItems
      ?.slice()
      .sort((a, b) => a.itemIndex - b.itemIndex)
      .map((c) => c.description?.trim())
      .find((d) => !!d) ?? CELL_DASH;
  const productsByUnit = taskProductsByUnit(task, bicCount, panelContext);
  const productValues: (string | ReactNode)[] = [];
  for (let unit = 1; unit <= bicCount; unit++) {
    productValues.push(productCellValue(productsByUnit.get(unit) ?? [], fallbackDescription));
  }

  const isCollectLike = task.type === 'Collect' || task.type === 'PickUp';
  const isLoad = task.type === 'Load';
  const isUnload = task.type === 'Unload';
  const isDeliverLike = task.type === 'Deliver' || task.type === 'DropOff';

  if (isCollectLike) {
    return [
      { label: 'Tank ID', values: tankIdValues, perUnit: true },
      { label: 'Tank Status', values: tankStatusValues, perUnit: true },
      { label: 'Capacity', values: capacityValues, perUnit: true },
      { label: 'Product', values: productValues, perUnit: true },
      tagsCellSpec(task, panelContext),
    ];
  }
  if (isLoad) {
    return [
      { label: 'Tank ID', values: tankIdValues, perUnit: true },
      { label: 'Tank Status', values: tankStatusValues, perUnit: true },
      { label: 'Load Weight', values: weightValues, perUnit: true },
      { label: 'Product', values: productValues, perUnit: true },
      tagsCellSpec(task, panelContext),
    ];
  }
  if (isUnload || isDeliverLike) {
    // Everything coming off each tank — two products off one tank are two
    // delivery rows but one Discharge.
    const dischargeByUnit = deliveryWeightByUnit(task, bicCount, panelContext);
    const dischargeValues: string[] = [];
    for (let unit = 1; unit <= bicCount; unit++) {
      const kg = dischargeByUnit.get(unit);
      dischargeValues.push(kg != null ? `${kg.toLocaleString('en-IE')} kg` : CELL_DASH);
    }
    return [
      { label: 'Tank ID', values: tankIdValues, perUnit: true },
      { label: 'Tank Status', values: tankStatusValues, perUnit: true },
      { label: 'Discharge', values: dischargeValues, perUnit: true },
      { label: 'Product', values: productValues, perUnit: true },
      { label: 'POD', values: [podPill(task.state, podStateOf(task, panelContext))], perUnit: false },
    ];
  }
  return [];
}

export function palletFamilyCells(task: MockTask, panelContext: TaskPanelContext): CellSpec[] {
  // Aggregate across every cargo item on the task — a delivery leg can carry
  // multiple allocation-derived lines (consolidation), so the header strip
  // sums quantity / LM / weight / volume rather than reading one row.
  const items = (task._source?.cargoItems ?? []).slice().sort((a, b) => a.itemIndex - b.itemIndex);

  const totalQty = items.reduce((s, c) => s + (c.quantity ?? 0), 0);
  const anyQty = items.some((c) => c.quantity != null);
  const unit = items.find((c) => c.quantityUnit)?.quantityUnit ?? 'pallet';
  const itemsValue = anyQty
    ? `${totalQty} ${unit}${totalQty === 1 ? '' : 's'}`
    : CELL_DASH;

  const weightSum = items.reduce((s, c) => s + (c.weightKg ?? 0), 0);
  const weightValue = items.some((c) => c.weightKg != null)
    ? `${weightSum.toLocaleString('en-IE')} kg`
    : CELL_DASH;

  const volumeSum = items.reduce((s, c) => s + (c.volumeM3 ?? 0), 0);
  const volumeValue = items.some((c) => c.volumeM3 != null) ? `${volumeSum} m³` : CELL_DASH;

  const referenceValue = task._source?.taskReference?.trim() || CELL_DASH;

  // Description: distinct cargo descriptions across the lines.
  const descs = Array.from(
    new Set(items.map((c) => c.description?.trim()).filter((d): d is string => !!d)),
  );
  const descriptionValue = descs.length === 0 ? CELL_DASH : descs.join(', ');

  // Loading metres: per item, prefer the persisted value, else derive from the
  // pallet variant's per-pallet metres × quantity (legacy rows). Sum the lot.
  let lmSum = 0;
  let hasLm = false;
  for (const c of items) {
    if (c.loadingMetresM != null) {
      lmSum += c.loadingMetresM;
      hasLm = true;
      continue;
    }
    const name = c.description?.trim();
    const per = name
      ? (panelContext.palletVariants.find((v) => v.name === name)?.loadingMetres ?? null)
      : null;
    if (per != null && c.quantity != null) {
      lmSum += per * c.quantity;
      hasLm = true;
    }
  }
  const lmValue = hasLm ? `${lmSum.toFixed(2)} m` : CELL_DASH;

  // Per-task temperature now persists on OrderTask.TemperatureC — show it
  // when set (temperature-controlled cargo), em-dash otherwise.
  const tempValue =
    task._source?.temperatureC != null ? `${task._source.temperatureC}°C` : CELL_DASH;

  // When the cargo type carries products, Product takes Volume's place —
  // every product on the task (a delivery's resolved from its collections).
  const productLabels = panelContext.productOptions != null
    ? (taskProductsByUnit(task, 1, panelContext).get(1) ?? [])
    : null;

  const baseCells: CellSpec[] = [
    { label: 'Items', values: [itemsValue], perUnit: false },
    { label: 'LM', values: [lmValue], perUnit: false },
    productLabels
      ? { label: 'Product', values: [productCellValue(productLabels, CELL_DASH)], perUnit: false }
      : { label: 'Volume', values: [volumeValue], perUnit: false },
    { label: 'Weight', values: [weightValue], perUnit: false },
    { label: 'Temp', values: [tempValue], perUnit: false },
    { label: 'Reference', values: [referenceValue], perUnit: false },
    { label: 'Description', values: [descriptionValue], perUnit: false },
  ];

  if (cargoHandlingPhaseFor(task._source?.type ?? 'PickUp') === 'delivery') {
    return [...baseCells, { label: 'POD', values: [podPill(task.state, podStateOf(task, panelContext))], perUnit: false }];
  }
  return [...baseCells, tagsCellSpec(task, panelContext)];
}

export function bookingDepartureCells(booking: MockBooking | null): CellSpec[] {
  const src = booking?._source;
  const crossingVal =
    src?.typicalDurationMinutes != null ? formatDuration(src.typicalDurationMinutes) : CELL_DASH;
  const checkInVal =
    src?.typicalCheckInMinutes != null ? formatDuration(src.typicalCheckInMinutes) : CELL_DASH;
  const typeVal = src?.bookingType ? (
    <StatusPill tone="blue" label={src.bookingType} />
  ) : (
    CELL_DASH
  );
  return [
    { label: 'Booking Ref', values: [booking?.reference ?? CELL_DASH], perUnit: false },
    { label: 'Carrier', values: [booking?.carrier ?? CELL_DASH], perUnit: false },
    { label: 'Check-in', values: [checkInVal], perUnit: false },
    { label: 'Crossing', values: [crossingVal], perUnit: false },
    { label: 'Type', values: [typeVal], perUnit: false },
  ];
}

export function bookingArrivalCells(
  booking: MockBooking | null,
  task: MockTask,
  cargoFamily: CargoFamily,
): CellSpec[] {
  let identifier: CellSpec;
  if (cargoFamily === 'container') {
    identifier = { label: 'BIC Code', values: [CELL_DASH], perUnit: false };
  } else if (cargoFamily === 'tank') {
    identifier = { label: 'Tank ID', values: [CELL_DASH], perUnit: false };
  } else {
    identifier = { label: 'Reference', values: [CELL_DASH], perUnit: false };
  }
  return [
    identifier,
    { label: 'Carrier', values: [booking?.carrier ?? CELL_DASH], perUnit: false },
    {
      label: 'ETA',
      values: [task.plannedTime ? formatClockTime(task.plannedTime) : CELL_DASH],
      perUnit: false,
    },
    {
      label: 'Customs',
      values: [booking?._source?.customsReference ?? CELL_DASH],
      perUnit: false,
    },
    { label: 'Clearance', values: [clearancePill(task.state)], perUnit: false },
  ];
}

/** "Mon 03 Jun"-style date label. */
export function formatTaskDate(iso: string | undefined): string {
  if (!iso) return '—';
  const parts = iso.split('-').map((n) => Number(n));
  const [y, m, d] = [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
  const date = new Date(Date.UTC(y, m - 1, d));
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  const day = String(d).padStart(2, '0');
  const month = date.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
  return `${weekday} ${day} ${month}`;
}

/** Compact duration: 240→"4h", 195→"3h 15m", 45→"45m". */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * `HH:mm` clock label from a wire time. The BE serialises `TimeOnly` as
 * `HH:mm:ss`, but planned times are only ever entered to the minute (the
 * forms use `<input type="time">`), so the seconds are always `:00`
 * noise. Tolerates an already-trimmed `HH:mm` for safety.
 */
export function formatClockTime(time: string): string {
  const [h, m] = time.split(':');
  if (h === undefined || m === undefined) return time;
  return `${h}:${m}`;
}

/**
 * Right-hand time label for the task row. Booking halves render
 * "ETA → ETD"; cargo tasks mirror the TimeModePicker pill labels.
 */
export function formatTaskTime(task: MockTask): string {
  if (task.type === 'BookingDeparture' || task.type === 'BookingArrival') {
    if (task.plannedTime && task.plannedDepartureTime) {
      return `${formatClockTime(task.plannedTime)} → ${formatClockTime(task.plannedDepartureTime)}`;
    }
    return task.plannedTime ? formatClockTime(task.plannedTime) : 'TBC';
  }
  if (task.timeMode === 'AM') return 'AM';
  if (task.timeMode === 'PM') return 'PM';
  if (task.timeMode === 'Exact' && task.plannedTime) return formatClockTime(task.plannedTime);
  return 'Any';
}

/**
 * Product labels per tank slot (1-based). A collection row's own lines; a
 * delivery row's source — the one line it draws from, or every line of the
 * collection row — grouped under the collection unit it came off, so two
 * products off one tank sit under that one tank.
 */
function taskProductsByUnit(
  task: MockTask,
  unitCount: number,
  panelContext: TaskPanelContext,
): Map<number, string[]> {
  const out = new Map<number, string[]>();
  const add = (slot: number, label: string) => {
    const s = unitCount <= 1 ? 1 : Math.min(Math.max(slot, 1), unitCount);
    const list = out.get(s) ?? [];
    if (!list.includes(label)) list.push(label);
    out.set(s, list);
  };
  const items = task._source?.cargoItems ?? [];
  if (cargoHandlingPhaseFor(task._source?.type ?? 'PickUp') === 'load') {
    for (const item of items) {
      for (const line of sortedLines(item)) add(item.itemIndex, productLineLabel(line));
    }
    return out;
  }

  // Delivery: resolve through the order's collection rows.
  const itemById = new Map<string, OrderTaskCargoItem>();
  const lineById = new Map<string, { item: OrderTaskCargoItem; line: OrderTaskCargoItemProduct }>();
  for (const sibling of panelContext.siblingTasks) {
    for (const item of sibling._source?.cargoItems ?? []) {
      itemById.set(item.id, item);
      for (const line of item.products ?? []) lineById.set(line.id, { item, line });
    }
  }
  for (const row of items) {
    const viaLine = row.sourceProductLineId ? lineById.get(row.sourceProductLineId) : undefined;
    if (viaLine) {
      add(viaLine.item.itemIndex, productLineLabel(viaLine.line));
      continue;
    }
    const source = row.sourceCollectionItemId ? itemById.get(row.sourceCollectionItemId) : undefined;
    if (source) for (const line of sortedLines(source)) add(source.itemIndex, productLineLabel(line));
  }
  return out;
}

function sortedLines(item: OrderTaskCargoItem): OrderTaskCargoItemProduct[] {
  return (item.products ?? []).slice().sort((a, b) => a.sequence - b.sequence);
}

/** One product as text; several as "first +N" with the full list on hover. */
function productCellValue(labels: string[], fallback: string): string | ReactNode {
  if (labels.length === 0) return fallback;
  if (labels.length === 1) return labels[0] ?? fallback;
  return (
    <span title={labels.join(", ")}>
      {labels[0]} <span className="font-semibold text-purple-700">+{labels.length - 1}</span>
    </span>
  );
}

/**
 * A delivery's weight per tank slot, grouped by the collection unit each row
 * draws from (rows with no source fall back to their own itemIndex).
 */
function deliveryWeightByUnit(
  task: MockTask,
  unitCount: number,
  panelContext: TaskPanelContext,
): Map<number, number> {
  const unitOfItem = new Map<string, number>();
  const unitOfLine = new Map<string, number>();
  for (const sibling of panelContext.siblingTasks) {
    for (const item of sibling._source?.cargoItems ?? []) {
      unitOfItem.set(item.id, item.itemIndex);
      for (const line of item.products ?? []) unitOfLine.set(line.id, item.itemIndex);
    }
  }
  const out = new Map<number, number>();
  for (const row of task._source?.cargoItems ?? []) {
    if (row.weightKg == null) continue;
    const unit =
      (row.sourceProductLineId ? unitOfLine.get(row.sourceProductLineId) : undefined) ??
      (row.sourceCollectionItemId ? unitOfItem.get(row.sourceCollectionItemId) : undefined) ??
      row.itemIndex;
    const slot = unitCount <= 1 ? 1 : Math.min(Math.max(unit, 1), unitCount);
    out.set(slot, (out.get(slot) ?? 0) + row.weightKg);
  }
  return out;
}

/**
 * The Tank ID(s) the job is carrying at an event that owns no cargo rows of
 * its own (a wash, a tank clean): the nearest EARLIER task in the sequence
 * that has one, else the next task after it — a wash before the first
 * collection washes the tank that collection will load.
 */
function tankIdsAtTask(task: MockTask, unitCount: number, panelContext: TaskPanelContext): string[] {
  const seq = task._source?.sequence ?? 0;
  const ordered = panelContext.siblingTasks
    .filter((t) => t.id !== task.id && t._source)
    .sort((a, b) => (a._source?.sequence ?? 0) - (b._source?.sequence ?? 0));
  const hasIds = (t: MockTask) => (t._source?.cargoItems ?? []).some((c) => c.identifier?.trim());
  const before = ordered.filter((t) => (t._source?.sequence ?? 0) < seq && hasIds(t)).pop();
  const after = ordered.find((t) => (t._source?.sequence ?? 0) > seq && hasIds(t));
  const from = before ?? after;
  const values: string[] = [];
  for (let unit = 1; unit <= unitCount; unit++) {
    values.push(from ? cargoFieldAt(from, unit, (c) => c.identifier) : CELL_DASH);
  }
  return values;
}
